import { useContext, type SyntheticEvent } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { useTranslate } from '@/hooks/useTranslate';
import { shouldCollectZipCodeForTaxCalculation } from '@/utils/tax';
import { getZipCodeErrorMessageKey } from '@/utils/zipCode';

/**
 * ZIP gate for the Chargebee wallets on `/subscriptions`.
 *
 * Both wallet buttons live in a Chargebee-owned iframe, so their click never
 * reaches us and can't be rejected mid-flight — the shopper would authorize a
 * US/CA total that still has no tax on it. `ExpressCheckoutRenderer` solved the
 * same problem for the express row with a transparent overlay
 * (`express-checkout-zip-blocker`), and `Paypal.tsx` does it for the PayPal
 * iframe; these wallets are now method boxes, outside both, so they carry the
 * overlay themselves.
 *
 * Unlike the express row this does NOT consult
 * `template.features.blockExpressWalletsForZipCode`: in a method box the wallet
 * IS the primary CTA, so an unresolved tax has to block it the way a disabled
 * pay button blocks the card form.
 */
export const useChargebeeWalletZipGate = (onInvalidTaxClick?: () => void) => {
  const {
    customer,
    pricing,
    addressDetails,
    actions: { setError },
  } = useContext(CheckoutContext);
  const { t } = useTranslate();

  const isBlocked = shouldCollectZipCodeForTaxCalculation(
    customer,
    addressDetails?.zipCode ?? customer.address?.zipCode,
    pricing.priceBreakdown.isTaxValid
  );

  const blockClick = (event: SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
    // The wrapper form highlights the ZIP input when the template shows the
    // error there; otherwise fall back to the global error banner.
    if (onInvalidTaxClick) {
      onInvalidTaxClick();
      return;
    }
    setError(t(getZipCodeErrorMessageKey(customer)));
  };

  return { isBlocked, blockClick };
};
